import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { MOCK_PRODUCTS } from '../data/mockData';
import { 
  Package, 
  Plus, 
  Check,
  Search,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function ProductAssignment({ open, onClose, door, building, floor }) {
  const { actions } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  const productTypes = ['all', ...new Set(MOCK_PRODUCTS.map(p => p.type))];

  const filteredProducts = MOCK_PRODUCTS.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || product.type === selectedType;
    return matchesSearch && matchesType;
  });

  const isProductAssigned = (productId) => {
    return door.products.includes(productId);
  };

  const handleToggleProduct = (productId) => {
    if (isProductAssigned(productId)) {
      actions.removeProductFromDoor(building.id, floor.id, door.id, productId);
      toast.success('Product removed');
    } else {
      actions.addProductToDoor(building.id, floor.id, door.id, productId);
      toast.success('Product added');
    }
  };

  const typeColors = {
    'handle': 'bg-blue-100 text-blue-700',
    'hinge': 'bg-amber-100 text-amber-700',
    'lock': 'bg-green-100 text-green-700',
    'closer': 'bg-purple-100 text-purple-700',
    'accessory': 'bg-slate-100 text-slate-700'
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px]" data-testid="product-assignment-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Add Products to {door.name}
          </SheetTitle>
          <SheetDescription>
            Select hardware products to assign to this door
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button 
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <div className="flex gap-2 flex-wrap">
            {productTypes.map(type => (
              <Button
                key={type}
                variant={selectedType === type ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType(type)}
                className="capitalize"
                data-testid={`filter-${type}`}
              >
                {type}
              </Button>
            ))}
          </div>

          {/* Products List */}
          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="space-y-2 pr-4">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No products found</p>
                </div>
              ) : (
                filteredProducts.map(product => {
                  const isAssigned = isProductAssigned(product.id);
                  const isCompatible = product.compatibleDoorTypes.includes(door.type);

                  return (
                    <div
                      key={product.id}
                      className={cn(
                        "border rounded-lg p-3 transition-all cursor-pointer",
                        isAssigned && "border-primary bg-primary/5",
                        !isAssigned && isCompatible && "border-border hover:border-primary/50",
                        !isCompatible && "border-border opacity-50"
                      )}
                      onClick={() => handleToggleProduct(product.id)}
                      data-testid={`product-item-${product.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{product.name}</span>
                            <Badge className={cn("text-xs", typeColors[product.type])}>
                              {product.type}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {product.description}
                          </p>
                          {!isCompatible && (
                            <p className="text-xs text-amber-600 mt-1">
                              Not typically used with {door.type} doors
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="font-semibold text-sm">
                            ${product.price.toFixed(2)}
                          </span>
                          <div className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center transition-colors",
                            isAssigned ? "bg-primary text-primary-foreground" : "border border-border"
                          )}>
                            {isAssigned ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Plus className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {/* Summary */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {door.products.length} products assigned
              </span>
              <Button onClick={onClose} data-testid="close-product-sheet-btn">
                Done
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
